import { useMemo } from "react";
import { useLimitOffsetFromPage } from "../../hooks/useLimitOffsetFromPage";
import { useQueryChatbots, Chatbot } from "./useQueryChatbots";
import { ChatbotsOrderBy, ChatbotsOrderDirection, Member } from "@zapier/bff";
import { hydrateMemberFromLookup } from "../member/deactivatedMemberOwner";
import { useMembers } from "../member/useMembers";

interface Input {
  ids?: string[];
  search?: string | null;
  page?: number | null;
  pageSize?: number;
  owner?: string | null;
  orderBy?: ChatbotsOrderBy;
  orderDirection?: ChatbotsOrderDirection;
}

export type HydratedChatbot = Chatbot & {
  owner?: Member;
};

export function useChatbots({
  ids,
  search,
  page,
  pageSize,
  owner,
  orderBy,
  orderDirection,
}: Input): {
  chatbots: HydratedChatbot[];
  isLoading: boolean;
  isFetching: boolean;
  meta: ReturnType<typeof useQueryChatbots>["meta"];
  loadError: ReturnType<typeof useQueryChatbots>["loadError"];
} {
  /** Pagination *********************************************** */
  const { limit, offset } = useLimitOffsetFromPage(page ?? 1, pageSize);

  /** Fetch Chatbots *********************************************** */

  const idsString = ids?.length ? ids?.join(",") : undefined;
  const {
    chatbots: fetchedChatbots,
    isLoading,
    isFetching,
    meta,
    loadError,
  } = useQueryChatbots({
    ...(idsString && { ids: idsString }),
    ...(search && { q: search }),
    ...(owner &&
      owner.startsWith("-") && {
        owner_customuser_id__not_in: Number.parseInt(owner.slice(1) as string),
      }),
    ...(owner &&
      !owner.startsWith("-") && {
        owner_customuser_id: Number.parseInt(owner as string),
      }),
    ...(orderBy && { orderBy }),
    ...(orderDirection && { orderDirection }),
    limit,
    offset,
  });

  /** Hydration *********************************************** */
  const dehydratedChatbots = useMemo(() => {
    return fetchedChatbots ?? [];
  }, [fetchedChatbots]);

  // Find all owner IDs for chatbots
  const memberIds = useMemo(
    () => [
      ...new Set(
        dehydratedChatbots
          ?.map((c) => c.ownerCustomuserId)
          .filter((id): id is number => id !== undefined),
      ),
    ],
    [dehydratedChatbots],
  );

  // Fetch all members for the owner IDs
  const { members, isPending: isMembersPending = false } = useMembers({
    ids: memberIds as number[],
  });

  const chatbots = useMemo(() => {
    return dehydratedChatbots?.map((chatbot) => {
      if (!chatbot.ownerCustomuserId) {
        return chatbot;
      }

      return {
        ...chatbot,
        owner: hydrateMemberFromLookup(members, chatbot.ownerCustomuserId, {
          deferDeactivatedUntilMembersResolve: true,
          isMembersPending,
        }),
      };
    });
  }, [dehydratedChatbots, members, isMembersPending]);

  return {
    chatbots,
    loadError,
    isLoading,
    isFetching,
    meta,
  };
}
